'use client'

import { useState } from 'react'
import { TextField, IconButton, InputAdornment } from '@mui/material'
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material'
import { Controller } from 'react-hook-form'

// Types
import type { TextFieldProps } from '@/types/components/molecules/inputs/text'

// Helpers
import { useTranslation } from '@/helpers/i18n/client'

// Utils
import GetPattern from '@/utils/get-pattern'

const TextFieldMolecule = (props: TextFieldProps) => {
	// Props
	const { lng, control, name, type, label, defaultValue, placeholder, helperText, min, max, multiline, rows, disabled, required, pattern, onKeyDown } = props

	// Variables
	const { t } = useTranslation(lng)
	const [showPassword, setShowPassword] = useState(false)

	// Check type
	let textType = type

	if (type === 'password') textType = !showPassword ? 'password' : 'text'
	else if (type === 'mobile') textType = 'tel'
	else if (type === 'alias') textType = 'text'

	return (
		<Controller
			control={control}
			name={name}
			defaultValue={defaultValue}
			disabled={disabled}
			rules={{
				required,
				pattern: pattern ? new RegExp(GetPattern(pattern, min, max)) : undefined
			}}
			render={({ field: { onChange, value, ref, disabled }, fieldState: { error } }) => (
				<TextField
					inputRef={ref}
					value={value}
					type={textType}
					variant="outlined"
					color="info"
					label={label && t(label)}
					helperText={!!error && helperText ? t(helperText ?? 'form:helper.error', { min, max }) : undefined}
					placeholder={placeholder || label ? t(placeholder ?? label ?? '') : undefined}
					error={!!error}
					multiline={multiline}
					rows={rows}
					required={required}
					disabled={disabled}
					focused
					fullWidth
					slotProps={{
						input: {
							endAdornment: type === 'password' && (
								<InputAdornment position="end">
									<IconButton onClick={() => setShowPassword(!showPassword)}>{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}</IconButton>
								</InputAdornment>
							)
						},
						inputLabel: {
							sx: {
								color: '#787a7c',
								'&.Mui-focused': { color: '#040404' }
							}
						},
						htmlInput: {
							maxLength: max,
							min,
							sx: {
								color: '#363738',
								borderRadius: 1,
								transition: 'all 0.3s ease',
								...(type === 'tel' && { textAlign: 'center' }),
								...(type === 'alias' && { textAlign: 'right' }),
								'&:hover': { color: '#000' },
								'&:focus': {
									color: '#000',
									bgcolor: '#fff'
								}
							}
						}
					}}
					onKeyDown={onKeyDown}
					onChange={event => {
						let value = event.target.value

						if (type === 'tel') value = value.replace(/\D/g, '')

						onChange(value)
					}}
					sx={{
						'& .MuiInputBase-root': {
							'&::before, &::after': { display: 'none' },
							'& fieldset': {
								borderWidth: 1.5,
								borderColor: '#ddd',
								transition: 'all 0.3s ease'
							},
							'&:hover': {
								'& fieldset': {
									borderWidth: 1.5,
									borderColor: '#aaa'
								}
							},
							'&.Mui-focused': {
								'& fieldset': {
									borderWidth: 1.5,
									borderColor: '#aaa'
								}
							}
						}
					}}
				/>
			)}
		/>
	)
}

export default TextFieldMolecule
